"use client";

import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";
import Icon from "@/components/ui/Icon";
import { COMPANY_INFO } from "@/data/companyInfo";

interface BlogAdBannerProps {
  slot?: string;
  position?: "top" | "mid" | "bottom" | "feed";
  format?: "auto" | "fluid" | "rectangle" | "horizontal";
  layout?: "in-article";
  responsive?: boolean;
  className?: string;
}

const POSITION_SLOTS: Record<NonNullable<BlogAdBannerProps["position"]>, string | undefined> = {
  top: process.env.NEXT_PUBLIC_ADSENSE_SLOT_TOP,
  mid: process.env.NEXT_PUBLIC_ADSENSE_SLOT_MID,
  bottom: process.env.NEXT_PUBLIC_ADSENSE_SLOT_BOTTOM,
  feed: process.env.NEXT_PUBLIC_ADSENSE_SLOT_FEED,
};

interface HouseAdConfig {
  badge: string;
  headline: string;
  subtext: string;
  ctaLabel: string;
  ctaHref: string;
  secondaryLabel: string;
  secondaryHref: string;
}

const HOUSE_ADS: Record<NonNullable<BlogAdBannerProps["position"]>, HouseAdConfig> = {
  top: {
    badge: "Enterprise Cloud & AI Audit",
    headline: "Cut AWS / GCP & LLM Inference Spend by 40–68% Without Latency Regressions",
    subtext:
      "Get a free 30-minute infrastructure, Kubernetes, or AI pipeline bottleneck audit with a Principal Systems Architect.",
    ctaLabel: "Book Free 30-Min Audit",
    ctaHref: "/book-consultation",
    secondaryLabel: "Explore DevOps & Cloud",
    secondaryHref: "/services/devops-cloud",
  },
  mid: {
    badge: "Production Engineering Pod",
    headline: "Shipping Next.js 16, Multi-Agent RAG, or High-Concurrency Microservices?",
    subtext:
      "Partner with Beyond Ambition Limited (NZBN 9429053304834) for fixed-milestone delivery and 99.99% SLA guarantees.",
    ctaLabel: "Scope Your Roadmap",
    ctaHref: "/book-consultation",
    secondaryLabel: "AI & LLM Services",
    secondaryHref: "/services/ai-ml",
  },
  bottom: {
    badge: "Dedicated Senior Engineers",
    headline: "Ready to Turn This Architecture Into Production-Grade Code?",
    subtext:
      "Deploy senior full-stack, mobile, and cloud engineers across the US, UK, AU & NZ timezones within 48 hours.",
    ctaLabel: "Schedule Discovery Call",
    ctaHref: "/book-consultation",
    secondaryLabel: "View Case Studies",
    secondaryHref: "/portfolio",
  },
  feed: {
    badge: "Featured Engineering Partner",
    headline: "Need Custom Software, AI Agents, or Cloud Migration? Talk to an Architect Today.",
    subtext:
      "Direct access to senior engineers — zero junior handoffs, transparent fixed-scope pricing, and rapid delivery.",
    ctaLabel: "Get a Free Technical Estimate",
    ctaHref: "/book-consultation",
    secondaryLabel: "WhatsApp an Architect",
    secondaryHref: COMPANY_INFO.whatsappLink,
  },
};

/**
 * Reusable Google AdSense in-article & feed ad unit with automatic House-Ad fallback.
 * - When AdSense slot IDs (`NEXT_PUBLIC_ADSENSE_SLOT_*`) are configured, renders lazy-loaded
 *   Google AdSense `<ins class="adsbygoogle">` units (`ca-pub-4039471957090769`) using
 *   IntersectionObserver (300px rootMargin) for >75% Active View viewability.
 * - When explicit slot IDs are not set yet (while AdSense Auto Ads handles automatic placement),
 *   renders a high-converting B2B House Ad / Consultation Banner so every slot monetizes immediately.
 */
export default function BlogAdBanner({
  slot,
  position = "top",
  format = "auto",
  layout,
  responsive = true,
  className = "",
}: BlogAdBannerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const adRef = useRef<HTMLModElement>(null);
  const pushedRef = useRef(false);
  const [isVisible, setIsVisible] = useState(
    () => typeof window !== "undefined" && !("IntersectionObserver" in window)
  );

  const resolvedSlot = slot || (position ? POSITION_SLOTS[position] : undefined);
  const houseAd = HOUSE_ADS[position] || HOUSE_ADS.top;

  useEffect(() => {
    if (!resolvedSlot || pushedRef.current || isVisible) return;
    const el = containerRef.current;
    if (!el || typeof window === "undefined" || !("IntersectionObserver" in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [resolvedSlot, isVisible]);

  useEffect(() => {
    if (!isVisible || !resolvedSlot || pushedRef.current) return;
    try {
      if (typeof window !== "undefined") {
        const w = window as unknown as { adsbygoogle?: unknown[] };
        w.adsbygoogle = w.adsbygoogle || [];
        w.adsbygoogle.push({});
        pushedRef.current = true;
      }
    } catch {
      // AdSense script is either loading or already handled by Auto Ads
    }
  }, [isVisible, resolvedSlot]);

  // If no explicit AdSense slot ID is set, render the high-converting B2B House Ad banner
  // while Google AdSense Auto Ads (`ca-pub-4039471957090769`) runs automatically on `/blog*`.
  if (!resolvedSlot) {
    const isExternalSecondary = houseAd.secondaryHref.startsWith("http");
    return (
      <div
        className={`my-8 w-full rounded-2xl border border-primary/25 bg-gradient-to-r from-surface-container-low via-surface-container to-primary/5 p-4 sm:p-5 shadow-sm ${className}`}
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1.5 max-w-2xl">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded bg-primary/15 text-primary font-bold">
                Sponsored · {houseAd.badge}
              </span>
            </div>
            <p className="text-sm sm:text-base font-bold text-on-surface leading-snug">
              {houseAd.headline}
            </p>
            <p className="text-xs sm:text-sm text-on-surface-variant leading-relaxed">
              {houseAd.subtext}
            </p>
          </div>
          <div className="flex flex-wrap sm:flex-nowrap items-center gap-2.5 shrink-0">
            <Link
              href={houseAd.ctaHref}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary hover:bg-primary-hover text-on-primary font-bold text-xs transition-all shadow-sm whitespace-nowrap"
            >
              <span>{houseAd.ctaLabel}</span>
              <Icon name="arrow_forward" size={14} />
            </Link>
            {isExternalSecondary ? (
              <a
                href={houseAd.secondaryHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2.5 rounded-xl border border-outline-variant/50 hover:border-primary/50 bg-surface/60 text-on-surface font-semibold text-xs transition-all whitespace-nowrap"
              >
                <span>{houseAd.secondaryLabel}</span>
              </a>
            ) : (
              <Link
                href={houseAd.secondaryHref}
                className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2.5 rounded-xl border border-outline-variant/50 hover:border-primary/50 bg-surface/60 text-on-surface font-semibold text-xs transition-all whitespace-nowrap"
              >
                <span>{houseAd.secondaryLabel}</span>
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`my-8 w-full min-h-[120px] flex flex-col items-center justify-center overflow-hidden rounded-xl border border-outline-variant/20 bg-surface-container-lowest/40 p-3 ${className}`}
    >
      <span className="text-[10px] text-on-surface-variant/60 uppercase tracking-widest mb-1.5 font-bold select-none">
        Sponsored · Advertisement
      </span>
      {isVisible && (
        <ins
          ref={adRef}
          className="adsbygoogle block w-full max-w-full text-center"
          style={{ display: "block", minHeight: "90px" }}
          data-ad-client="ca-pub-4039471957090769"
          data-ad-slot={resolvedSlot}
          data-ad-format={format}
          data-ad-layout={layout}
          data-full-width-responsive={responsive ? "true" : "false"}
        />
      )}
    </div>
  );
}
